import { useMemo, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Alert,
  FormControlLabel,
  Switch,
  TextField,
  InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/SearchOutlined';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import ArchiveIcon from '@mui/icons-material/ArchiveOutlined';
import UnarchiveIcon from '@mui/icons-material/UnarchiveOutlined';
import { useTranslation } from 'react-i18next';
import type { Tag } from '@altyor-kronos/shared-types';
import { useCollectionSnapshot } from '../../core/data/useCollectionSnapshot';
import { createTag, setTagArchived } from '../../core/data/adminMutations';
import { adminDataGridSx } from './adminDataGridSx';

/**
 * Admin-only catalogue of Kronos time-entry tags (admins only — the whole
 * /settings page is gated by RequireAdmin). Mirrors DesignationCatalogTab
 * but simpler: tags live on entries as plain label strings, so there is no
 * rename (it would orphan existing entries) and no delete (archive instead,
 * keeping history intact). Add + archive/restore is the full surface.
 */
export default function TagsCatalogTab() {
  const { t } = useTranslation();
  const { rows, loading, error } = useCollectionSnapshot<Tag>('tags');

  const [searchQuery, setSearchQuery] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [addError, setAddError] = useState<string | null>(null);

  const gridRows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return rows
      .filter(({ data }) => showArchived || !data.isArchived)
      .filter(({ data }) => !q || data.label.toLowerCase().includes(q))
      .map(({ id, data }) => ({ id, label: data.label, isArchived: Boolean(data.isArchived) }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [rows, searchQuery, showArchived]);

  const onAdd = async () => {
    const label = newLabel.trim();
    if (!label) return;
    const exists = rows.some(({ data }) => data.label.toLowerCase() === label.toLowerCase());
    if (exists) {
      setAddError(t('admin.tags.duplicate'));
      return;
    }
    setAddError(null);
    setNewLabel('');
    await createTag(label);
  };

  const toggleArchive = async (id: string, currentlyArchived: boolean) => {
    await setTagArchived(id, !currentlyArchived);
  };

  const columns: GridColDef[] = [
    { field: 'label', headerName: t('admin.tags.name'), flex: 1, minWidth: 250 },
    {
      field: 'actions',
      headerName: '',
      width: 70,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <IconButton
          size="small"
          aria-label={params.row.isArchived ? t('admin.tags.restore') : t('admin.tags.archive')}
          onClick={() => toggleArchive(params.row.id, params.row.isArchived)}
        >
          {params.row.isArchived ? (
            <UnarchiveIcon fontSize="small" />
          ) : (
            <ArchiveIcon fontSize="small" />
          )}
        </IconButton>
      ),
    },
  ];

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: { xs: 'stretch', sm: 'flex-start' },
          gap: { xs: 1, sm: 2 },
          flexWrap: 'wrap',
          mb: 2,
        }}
      >
        <TextField
          size="small"
          label={t('admin.tags.namePlaceholder')}
          value={newLabel}
          error={Boolean(addError)}
          helperText={addError ?? ' '}
          onChange={(e) => {
            setNewLabel(e.target.value);
            if (addError) setAddError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void onAdd();
            }
          }}
          sx={{ width: { xs: '100%', sm: 280 } }}
        />
        <Button
          variant="contained"
          color="primary"
          onClick={() => void onAdd()}
          disabled={!newLabel.trim()}
          sx={{ flexShrink: 0, mt: { sm: 0.25 } }}
        >
          {t('admin.tags.add')}
        </Button>
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: { xs: 1, sm: 2 },
          flexWrap: 'wrap',
          mb: 1,
        }}
      >
        <TextField
          size="small"
          placeholder={t('admin.tags.searchPlaceholder')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ opacity: 0.5 }} />
                </InputAdornment>
              ),
            },
          }}
          sx={{ width: { xs: '100%', sm: 260 }, flex: { xs: '1 1 100%', sm: '0 1 auto' } }}
        />
        <FormControlLabel
          control={
            <Switch
              checked={showArchived}
              onChange={(_, checked) => setShowArchived(checked)}
              size="small"
            />
          }
          label={t('admin.tags.showArchived')}
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error.message}
        </Alert>
      )}
      <Box sx={{ height: 'calc(100vh - 360px)', minHeight: 360 }}>
        <DataGrid
          rows={gridRows}
          columns={columns}
          loading={loading}
          disableRowSelectionOnClick
          disableColumnSelector
          density="standard"
          sx={{
            ...adminDataGridSx,
            '& .archived-row': { opacity: 0.5 },
          }}
          getRowClassName={(params) => (params.row.isArchived ? 'archived-row' : '')}
          hideFooter
        />
      </Box>
    </Box>
  );
}
